import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { CollapsedSidebar } from "./CollapsedSidebar";
import {
  Users,
  FileText,
  ShieldCheck,
  CreditCard,
  Settings,
  HelpCircle,
  LogOut,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { CreditBalance } from "@/components/credits";

interface NewAppSidebarProps {
  onLogout: () => void;
  onNavigate: (route: string) => void;
  onBuyCredits?: () => void;
}

export const NewAppSidebar = ({ onLogout, onNavigate, onBuyCredits }: NewAppSidebarProps) => {
  const location = useLocation();
  const { profile } = useAuth();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const mainMenuItems = [
    { icon: Users, label: 'Mon équipe', path: '/mon-equipe' },
    { icon: Search, label: 'Prise de référence', path: '/dashboard' },
    { icon: FileText, label: 'Rapport', path: '/reports' },
    { icon: ShieldCheck, label: 'Conformité', path: '/conformite' },
    { icon: CreditCard, label: 'Abonnement', path: '/subscription' },
  ];

  const settingsMenuItems = [
    { icon: Settings, label: 'Paramètres', path: '/settings' },
    { icon: HelpCircle, label: 'Support', path: '/help-support' },
  ];

  // Gestionnaire pour l'achat de crédits - ouvre la modal si disponible, sinon navigue
  const handleBuyCredits = () => {
    if (onBuyCredits) {
      onBuyCredits();
    } else {
      onNavigate('/subscription');
    }
  };

  // If collapsed, use the new collapsed design
  if (isCollapsed) {
    return (
      <CollapsedSidebar
        onLogout={onLogout}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <Sidebar
      className="fixed left-0 top-0 z-50 h-screen flex-shrink-0 border-r border-[hsl(var(--app-sidebar-border))]"
      collapsible="icon"
      style={{ width: '228px', backgroundColor: 'hsl(var(--app-sidebar-bg))' }}
    >
      <SidebarContent
        className="flex h-full flex-col px-3.5 py-4"
        style={{ backgroundColor: 'hsl(var(--app-sidebar-bg))' }}
      >
        <SidebarGroup>
          {/* Header avec Logo */}
          <div className="mb-6 flex items-center px-2">
            <img
              src="https://pvmemyboxgefsaordzqj.supabase.co/storage/v1/object/public/App%20image/Logo%20Getproof%20White.svg"
              alt="Getproof Logo"
              className="h-7 cursor-pointer opacity-95 transition-opacity hover:opacity-80"
              onClick={() => onNavigate('/mon-equipe')}
            />
          </div>

          {/* Menu principal */}
          <SidebarMenu className="space-y-1">
            {mainMenuItems.map((item) => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  onClick={() => onNavigate(item.path)}
                  isActive={location.pathname === item.path}
                  className={`h-10 w-full justify-start rounded-xl px-3 py-2 text-[13px] font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'border text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                      : 'text-[hsl(var(--app-sidebar-text-muted))] hover:bg-white/[0.035] hover:text-[hsl(var(--app-sidebar-text))]'
                  }`}
                  style={
                    location.pathname === item.path
                      ? {
                          backgroundColor: 'hsl(var(--app-sidebar-surface))',
                          borderColor: 'rgba(255,255,255,0.06)',
                          color: '#FFFFFF',
                        }
                      : {}
                  }
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  <span className="font-medium">{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>

          {/* Divider */}
          <div className="my-4 px-2">
            <div className="h-px bg-white/8"></div>
          </div>

          {/* Menu paramètres */}
          <SidebarMenu className="space-y-1">
            {settingsMenuItems.map((item) => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  onClick={() => onNavigate(item.path)}
                  isActive={location.pathname === item.path}
                  className={`h-10 w-full justify-start rounded-xl px-3 py-2 text-[13px] font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'border text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                      : 'text-[hsl(var(--app-sidebar-text-muted))] hover:bg-white/[0.035] hover:text-[hsl(var(--app-sidebar-text))]'
                  }`}
                  style={
                    location.pathname === item.path
                      ? {
                          backgroundColor: 'hsl(var(--app-sidebar-surface))',
                          borderColor: 'rgba(255,255,255,0.06)',
                          color: '#FFFFFF',
                        }
                      : {}
                  }
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  <span className="font-medium">{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>

          {/* Credits Section avec mises à jour en temps réel */}
          <CreditBalance 
            onBuyCredits={handleBuyCredits}
            className="mt-8"
          />
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-white/[0.06] px-3.5 py-3.5" style={{ backgroundColor: 'hsl(var(--app-sidebar-bg))' }}>
        <div className="flex items-center space-x-2.5 rounded-2xl border border-white/[0.05] bg-white/[0.03] px-2.5 py-2.5">
          <Avatar className="h-9 w-9">
            <AvatarImage src={profile?.avatar_url || undefined} alt="Photo de profil" />
            <AvatarFallback className="text-white font-medium" style={{ backgroundColor: '#40E0D0' }}>
              {profile?.first_name?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="truncate text-[13px] font-medium text-white">
              {profile?.first_name || 'Muhammad Utban'}
            </p>
            <p className="truncate text-xs text-[hsl(var(--app-sidebar-text-muted))]">
              {profile?.email || 'randuutban07@gmail.com'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="p-2 text-[#f28c94] hover:bg-white/5 hover:text-[#ffb1b8]"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};
